import { Chip, Divider } from '@heroui/react';
import VesselsSelectbox from '../components/portfolio-detail/Vessels-Selectbox';
import SelectionSave from '../components/portfolio-detail/Selection-Save';
import SelectionApply from '../components/portfolio-detail/Selection-Apply';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { differenceBy, filter, uniqueId, sortBy, isEqual } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import Label from '../components/Label';
import FileUpload from '../components/portfolio-detail/File-Upload';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { AppDispatch } from '../../../core/store';
import { IPortfolioVesselForm, IPortfolioWithVesselsForm } from '../portfolioTypes';
import {
	selectSelectedPortfolioWithMsiVessels,
	selectPortfolioNames,
	selectIsPortfolioWithVesselsLoading,
	selectIsPortfolioBusy,
	selectSubscribedMsiVesselFormList,
} from '../portfolioSelectors';
import ModalManager from '../../../shared/components/modal/ModalManager';
import {
	addPortfolioRequest,
	addDraftPortfolioRequest,
	updateDraftPortfolioRequest,
	setSelectedPortfolioRequest,
	updatePortfolioRequest,
} from '../portfolioSlice';
import SharedSpinner from '../../../shared/components/spinner/SharedSpinner';
import { selectUserId } from '../../../user-management/appSelectors';
import { setSelectedVesselsForOutputRequest } from '../../search/searchSlice';
import { EOutputType, ESelectionType } from '../../../shared/sharedEnums';
import { IPortfolioFormData, IVesselFormData } from '../../../shared/sharedTypes';

export interface IPortfolioDetailPanelProps {
	closePortfolioDetail: () => void;
	portfolioId: number | null;
	displayPortfolioMainPanel: (value: string) => void;
}

const PortfolioDetailPanel: React.FC<IPortfolioDetailPanelProps> = ({
	portfolioId,
	closePortfolioDetail,
	displayPortfolioMainPanel,
}) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const itemsPerLoad = 100;

	// Selectors to get the selected portfolio with vessels and loading status
	const subscribedMsiVesselFormList: IPortfolioVesselForm[] = useAppSelector(selectSubscribedMsiVesselFormList);
	const selectedPortfolioWithVessels: IPortfolioWithVesselsForm | null = useAppSelector(selectSelectedPortfolioWithMsiVessels);
	const isPortfolioWithVesselsLoading = useAppSelector(selectIsPortfolioWithVesselsLoading);
	const isBusy = useAppSelector(selectIsPortfolioBusy);
	const portfolioNames: string[] = useAppSelector(selectPortfolioNames);

	const [leftItems, setLeftItems] = useState<IPortfolioVesselForm[]>([]);
	const [rightItems, setRightItems] = useState<IPortfolioVesselForm[]>([]);

	const [portfolioVessels, setPortfolioVessels] = useState<IPortfolioVesselForm[]>([]);

	// const [selectedPortfolioVesselKeys, setSelectedPortfolioVesselKeys] = useState<Set<string>>(new Set([]));
	const [selectedMsiVesselsKeys, setSelectedMsiVesselKeys] = useState<Set<string>>(new Set([]));

	// Local references to keep track of the offset and search term
	const msiVesselsOffsetRef = useRef(0);
	const msiVesselsSearchTermRef = useRef('');
	const msiVesselsListboxRef = useRef<HTMLDivElement>(null);
	// const initialPortfolioWithVesselsForm = useRef<IPortfolioWithVesselsForm | null>(null);

	const portfolioVesselsOffsetRef = useRef(0);
	const portfolioVesselsSearchTermRef = useRef('');
	const portfolioVesselsListboxRef = useRef<HTMLDivElement>(null);

	const currentUserId: number | undefined = useAppSelector(selectUserId);

	// Form hook to keep track of the form values and errors
	const {
		register,
		handleSubmit,
		setValue,
		reset,
		trigger,
		formState: { errors },
	} = useForm({
		mode: 'onSubmit',
		defaultValues: {
			name: selectedPortfolioWithVessels?.name || '',
			scope: selectedPortfolioWithVessels?.scope || '',
			portfolioVesselsImoList: selectedPortfolioWithVessels?.portfolioWithMsiVesselsForm?.map((v) => v.imoNumber),
		},
	});

	useEffect(() => {
		setValue(
			'portfolioVesselsImoList',
			selectedPortfolioWithVessels?.portfolioWithMsiVesselsForm?.map((v) => v.imoNumber),
		);
		// setSelectedScope(selectedPortfolioWithVessels?.scope || '');
		// setPortfolioName(selectedPortfolioWithVessels?.name || '');
		setSelectedMsiVesselKeys(
			new Set(selectedPortfolioWithVessels?.portfolioWithMsiVesselsForm?.map((v) => v.imoNumber.toString()) ?? []),
		);
	}, [selectedPortfolioWithVessels, setValue]);

	const initializePortfolioVessels = useCallback(() => {
		// Get already selected vessels from portfolio
		const rightItems: IPortfolioVesselForm[] =
			{ ...selectedPortfolioWithVessels }?.portfolioWithMsiVesselsForm?.map((vessel) => {
				const subscribedVessel = subscribedMsiVesselFormList.find((subVessel) => subVessel.imoNumber === vessel.imoNumber);
				return subscribedVessel ? { ...vessel, ...subscribedVessel } : vessel;
			}) ?? [];
		// initialPortfolioWithVesselsForm.current = selectedPortfolioWithVessels;
		const leftItems = [...subscribedMsiVesselFormList].slice(0, itemsPerLoad);
		msiVesselsOffsetRef.current = itemsPerLoad;
		setLeftItems(leftItems);
		setRightItems(rightItems);
		setPortfolioVessels(rightItems);
		setSelectedMsiVesselKeys(new Set(rightItems.map((v) => v.imoNumber.toString())));
	}, [selectedPortfolioWithVessels, subscribedMsiVesselFormList]);

	useEffect(() => {
		initializePortfolioVessels();
	}, [initializePortfolioVessels, subscribedMsiVesselFormList]);

	const getFilteredMsiVessels = useCallback(
		(searchTerm: string) => {
			// Filter subscribed vessels by search term
			let filteredLeftItems: IPortfolioVesselForm[] = [...subscribedMsiVesselFormList];
			filteredLeftItems =
				searchTerm !== ''
					? filter(
							filteredLeftItems,
							(item) =>
								item.vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
								item.imoNumber.toString().includes(searchTerm) ||
								item.sectorName.toLowerCase().includes(searchTerm.toLowerCase()),
						)
					: filteredLeftItems;
			return filteredLeftItems;
		},
		[subscribedMsiVesselFormList],
	);

	const getFilteredPortfolioVessels = useCallback(
		(searchTerm: string) => {
			// Filter subscribed vessels by search term
			let filteredRightItems: IPortfolioVesselForm[] = [
				...(portfolioVessels?.map((v) => ({
					imoNumber: v.imoNumber,
					vesselName: v.vesselName,
					sectorName: v.sectorName,
					companyName: v.companyName,
				})) ?? []),
			];
			filteredRightItems =
				searchTerm !== ''
					? filter(
							filteredRightItems,
							(item) =>
								item.vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
								item.imoNumber.toString().includes(searchTerm) ||
								item.sectorName.toLowerCase().includes(searchTerm.toLowerCase()),
						)
					: filteredRightItems;
			return filteredRightItems;
		},
		[portfolioVessels],
	);

	// const loadMsiVesselsOnScroll = useCallback((listboxRef: any) => {

	//     const listbox = listboxRef.currentTarget;
	//     if (!listbox) return;

	//     const { scrollTop, scrollHeight, clientHeight } = listbox;

	//     // If user scrolls to the bottom and there are more items to load
	//     if (scrollTop + clientHeight >= scrollHeight && msiVesselsOffsetRef.current < getFilteredMsiVessels(msiVesselsSearchTermRef.current).length) {
	//         const filteredLeftItems = getFilteredMsiVessels(msiVesselsSearchTermRef.current);
	//         setLeftItems((prevItems) => [
	//             ...prevItems,
	//             ...filteredLeftItems.slice(msiVesselsOffsetRef.current, msiVesselsOffsetRef.current + itemsPerLoad)]);

	//         msiVesselsOffsetRef.current = msiVesselsOffsetRef.current + itemsPerLoad;
	//     }
	// }, [getFilteredMsiVessels, itemsPerLoad])

	const loadMsiVesselsOnScroll = useCallback(
		(event: React.UIEvent<HTMLElement>) => {
			const listbox = event.currentTarget;
			if (!listbox) return;

			const { scrollTop, scrollHeight, clientHeight } = listbox;

			const isNearBottom = scrollTop + clientHeight >= scrollHeight - 1;

			if (isNearBottom && msiVesselsOffsetRef.current < getFilteredMsiVessels(msiVesselsSearchTermRef.current).length) {
				const filteredLeftItems = getFilteredMsiVessels(msiVesselsSearchTermRef.current);

				setLeftItems((prevItems) => [
					...prevItems,
					...filteredLeftItems.slice(msiVesselsOffsetRef.current, msiVesselsOffsetRef.current + itemsPerLoad),
				]);

				msiVesselsOffsetRef.current += itemsPerLoad;
			}
		},
		[getFilteredMsiVessels, itemsPerLoad],
	);

	const loadPortfolioVesselsOnScroll = useCallback(
		(listboxRef: any) => {
			const listbox = listboxRef.currentTarget;
			if (!listbox) return;

			const { scrollTop, scrollHeight, clientHeight } = listbox;

			// If user scrolls to the bottom and there are more items to load
			if (scrollTop + clientHeight >= scrollHeight && msiVesselsOffsetRef.current < rightItems.length) {
				const filteredRightItems = getFilteredPortfolioVessels(portfolioVesselsSearchTermRef.current);
				setRightItems((prevItems) => [
					...prevItems,
					...filteredRightItems.slice(portfolioVesselsOffsetRef.current, portfolioVesselsOffsetRef.current + itemsPerLoad),
				]);

				portfolioVesselsOffsetRef.current = portfolioVesselsOffsetRef.current + itemsPerLoad;
			}
		},
		[getFilteredPortfolioVessels, rightItems, itemsPerLoad],
	);

	const filterMsiVesselsOnSearch = useCallback(
		(searchTerm: string) => {
			msiVesselsOffsetRef.current = 0;
			const filteredLeftItems = getFilteredMsiVessels(searchTerm);
			const filteredItems = filteredLeftItems.slice(msiVesselsOffsetRef.current, itemsPerLoad);
			msiVesselsOffsetRef.current = msiVesselsOffsetRef.current + itemsPerLoad;
			msiVesselsSearchTermRef.current = searchTerm;
			const msiVesselsListbox = msiVesselsListboxRef.current;

			if (msiVesselsListbox) {
				msiVesselsListbox.scrollTop = 0;
			}
			setLeftItems(filteredItems);
		},
		[getFilteredMsiVessels, itemsPerLoad],
	);

	// filter portfolio vessels on search
	const filterPortfolioVesselsOnSearch = useCallback(
		(searchTerm: string) => {
			portfolioVesselsOffsetRef.current = 0;
			const filteredRightItems = getFilteredPortfolioVessels(searchTerm);
			const filteredItems = filteredRightItems.slice(portfolioVesselsOffsetRef.current, itemsPerLoad);
			portfolioVesselsOffsetRef.current = portfolioVesselsOffsetRef.current + itemsPerLoad;
			portfolioVesselsSearchTermRef.current = searchTerm;
			const portfolioVesselsListbox = portfolioVesselsListboxRef.current;

			if (portfolioVesselsListbox) {
				portfolioVesselsListbox.scrollTop = 0;
			}
			setRightItems(filteredItems);
		},
		[getFilteredPortfolioVessels, itemsPerLoad],
	);

	// Update selected keys when user selects a vessel or uploads a file by adding to already saved selected keys
	//TODO: There is a rerendering issue here, when other components are rerendered, this component is rerendered
	const filterMsiVesselsOnUpload = useCallback(
		(imoNumbers: string[]) => {
			const mergedImoNumbers = [...selectedMsiVesselsKeys, ...imoNumbers];

			//TODO: filter and check if the imoNumbers are already in the selectedKeys or not found
			setSelectedMsiVesselKeys(new Set(mergedImoNumbers));

			if (imoNumbers.length > 0) {
				const tempRightItems = filter([...subscribedMsiVesselFormList], (item) => imoNumbers.includes(item.imoNumber.toString()));

				portfolioVesselsOffsetRef.current = 0;
				setRightItems([...tempRightItems].slice(portfolioVesselsOffsetRef.current, itemsPerLoad));
				setPortfolioVessels([...tempRightItems]);
				setValue(
					'portfolioVesselsImoList',
					tempRightItems.map((v) => v.imoNumber),
				);
			}
		},
		[selectedMsiVesselsKeys],
	);

	const discardChanges = () => {
		reset();
		if (portfolioId === null) {
			closePortfolioDetail();
		} else {
			dispatch(setSelectedPortfolioRequest({ id: portfolioId }));
		}
	};

	const onDisplayPortfolioOutputSubmit: SubmitHandler<any> = (data) => {
		if (!trigger()) {
			return;
		}

		if (data.portfolioVesselsImoList.length === 0) {
			ModalManager.show({
				header: 'Warning',
				message: 'No vessels found in Portfolio. Please add vessels to your portfolio or contact MSI.',
				showActionButton: false,
				size: 'sm',
			});
			return;
		}
		let tempId = undefined;
		let selectionType = ESelectionType.Portfolio;
		let isDraft = selectedPortfolioWithVessels.isDraft;
		if (
			selectedPortfolioWithVessels?.id ||
			(selectedPortfolioWithVessels?.id === null &&
				selectedPortfolioWithVessels?.tempId &&
				selectedPortfolioWithVessels?.isDraft === true)
		) {
			// update filter on state only as draft

			const portfolio = {
				id: selectedPortfolioWithVessels.id,
				name: data.name,
				scope: data.scope,
				portfolioVesselsImoList: data.portfolioVesselsImoList,
				isOwnPortfolio: selectedPortfolioWithVessels.isOwnPortfolio,
				isDraft: true,
				tempId: selectedPortfolioWithVessels?.tempId,
			};

			const isPortfolioChanged =
				!isEqual(
					sortBy(data.portfolioVesselsImoList),
					sortBy(selectedPortfolioWithVessels.portfolioWithMsiVesselsForm?.map((v) => v.imoNumber)),
				) ||
				!isEqual(data.name, selectedPortfolioWithVessels.name) ||
				!isEqual(data.scope, selectedPortfolioWithVessels.scope);

			if (isPortfolioChanged) {
				dispatch(updateDraftPortfolioRequest({ portfolio }));
				selectionType = ESelectionType.DraftPortfolio;
				isDraft = true;
			}
		} else {
			// add draft portfolio - no save -submit
			tempId = uniqueId('temp-');
			const portfolio = {
				id: null,
				name: data.name,
				scope: data.scope,
				portfolioVesselsImoList: data.portfolioVesselsImoList,
				createdById: currentUserId,
				isOwnPortfolio: true,
				isDraft: true,
				tempId: tempId,
			};
			dispatch(addDraftPortfolioRequest({ portfolio }));
			selectionType = ESelectionType.DraftPortfolio;
			isDraft = true;
		}

		dispatch(
			setSelectedVesselsForOutputRequest({
				outputSelection: {
					selectedVesselsList: portfolioVessels.map((v) => ({
						name: v.vesselName,
						imoNumber: v.imoNumber,
						sectorName: v.sectorName,
						companyName: v.companyName,
					})) as IVesselFormData[],
					selectionName: data.name,
					selectionType: selectedPortfolioWithVessels.id === null || isDraft ? ESelectionType.DraftPortfolio : selectionType,
					outputType:
						portfolioVessels.length === 1
							? EOutputType.Vessel
							: portfolioVessels.length > 200
								? EOutputType.Summary
								: EOutputType.Fleet,
					selectionTypeObject: {
						id: selectedPortfolioWithVessels.id,
						name: data.name,
						scope: data.scope,
						isOwnPortfolio: selectedPortfolioWithVessels.isOwnPortfolio,
						isDraft: isDraft,
						tempId: tempId,
					} as IPortfolioFormData,
				},
			}),
		);
		closePortfolioDetail();
		displayPortfolioMainPanel('');
	};

	const onSaveSubmit: SubmitHandler<any> = (data) => {
		// if scope is team, set teamId to user's teamId otherwise set it to null
		if (selectedPortfolioWithVessels?.id) {
			// update portfolio
			const portfolio = {
				id: selectedPortfolioWithVessels.id,
				name: data.name,
				scope: data.scope,
				portfolioVesselsImoList: data.portfolioVesselsImoList,
				isOwnPortfolio: selectedPortfolioWithVessels.isOwnPortfolio,
				isDraft: false,
			};

			const isPortfolioChanged =
				!isEqual(
					sortBy(data.portfolioVesselsImoList),
					sortBy(selectedPortfolioWithVessels.portfolioWithMsiVesselsForm?.map((v) => v.imoNumber)),
				) ||
				!isEqual(data.name, selectedPortfolioWithVessels.name) ||
				!isEqual(data.scope, selectedPortfolioWithVessels.scope);

			if (!isPortfolioChanged && !selectedPortfolioWithVessels.isDraft) {
				ModalManager.show({
					header: 'Warning',
					message: 'No changes found in Portfolio.',
					showActionButton: false,
					size: 'xs',
				});
				return;
			} else {
				dispatch(updatePortfolioRequest({ portfolio }));
			}
		} else {
			// add portfolio
			const portfolio = {
				id: null,
				name: data.name,
				scope: data.scope,
				portfolioVesselsImoList: data.portfolioVesselsImoList,
				isDraft: false,
				tempId: selectedPortfolioWithVessels?.tempId,
			};
			dispatch(addPortfolioRequest({ portfolio }));
		}
	};

	const updateSelectedMsiVesselKeys = useCallback(
		(imoNumbers: string[]) => {
			setSelectedMsiVesselKeys(new Set(imoNumbers));

			if (imoNumbers.length > 0) {
				const tempRightItems = filter([...subscribedMsiVesselFormList], (item) => imoNumbers.includes(item.imoNumber.toString()));

				portfolioVesselsOffsetRef.current = 0;
				setRightItems([...tempRightItems].slice(portfolioVesselsOffsetRef.current, itemsPerLoad));
				setPortfolioVessels([...tempRightItems]);
				setValue(
					'portfolioVesselsImoList',
					tempRightItems.map((v) => v.imoNumber),
				);
			}
		},
		[subscribedMsiVesselFormList],
	);

	const updateSelectedPortfolioVesselKeys = useCallback(
		(imoNumbers: string[]) => {
			const tempRightItems = filter([...subscribedMsiVesselFormList], (item) => imoNumbers.includes(item.imoNumber.toString()));

			let filteredRightItems = differenceBy(portfolioVessels, tempRightItems, 'imoNumber');
			setSelectedMsiVesselKeys(new Set(filteredRightItems.map((v) => v.imoNumber.toString())));

			setPortfolioVessels(filteredRightItems);
			setValue(
				'portfolioVesselsImoList',
				filteredRightItems.map((v) => v.imoNumber),
			);
			filteredRightItems =
				portfolioVesselsSearchTermRef.current !== ''
					? filter(
							filteredRightItems,
							(item) =>
								item.vesselName.toLowerCase().includes(portfolioVesselsSearchTermRef.current.toLowerCase()) ||
								item.imoNumber.toString().includes(portfolioVesselsSearchTermRef.current) ||
								item.sectorName.toLowerCase().includes(portfolioVesselsSearchTermRef.current.toLowerCase()),
						)
					: filteredRightItems;
			portfolioVesselsOffsetRef.current = 0;
			setRightItems(filteredRightItems.slice(portfolioVesselsOffsetRef.current, itemsPerLoad));
		},
		[subscribedMsiVesselFormList, portfolioVessels],
	);

	// const moveToPortfolioVessels = () => {
	//     if (selectedMsiVesselsKeys) {
	//         const tempRightItems = filter(
	//             [...subscribedMsiVesselFormList],
	//             (item) => selectedMsiVesselsKeys.has(item.imoNumber.toString())
	//         );

	//         portfolioVesselsOffsetRef.current = 0;
	//         setRightItems([...tempRightItems].slice(portfolioVesselsOffsetRef.current, itemsPerLoad));
	//         setPortfolioVessels([...tempRightItems]);
	//         setValue('portfolioVesselsImoList', tempRightItems.map(v => v.imoNumber));
	//     }
	// };

	// const removeFromMsiVessels = () => {
	//     if (selectedPortfolioVesselKeys) {
	//         const tempRightItems = filter(
	//             [...subscribedMsiVesselFormList],
	//             (item) => selectedPortfolioVesselKeys.has(item.imoNumber.toString())
	//         );
	//         let filteredRightItems = differenceBy(portfolioVessels, tempRightItems, "imoNumber");
	//         setSelectedMsiVesselKeys(new Set(filteredRightItems.map(v => v.imoNumber.toString())));
	//         setPortfolioVessels(filteredRightItems);
	//         setValue('portfolioVesselsImoList', filteredRightItems.map(v => v.imoNumber));
	//         filteredRightItems = portfolioVesselsSearchTermRef.current !== "" ? filter(filteredRightItems, (item) =>
	//             item.vesselName.toLowerCase().includes(portfolioVesselsSearchTermRef.current.toLowerCase())
	//             || item.imoNumber.toString().includes(portfolioVesselsSearchTermRef.current)
	//             || item.sectorName.toLowerCase().includes(portfolioVesselsSearchTermRef.current.toLowerCase())
	//         ) : filteredRightItems;
	//         portfolioVesselsOffsetRef.current = 0;
	//         setRightItems(filteredRightItems.slice(portfolioVesselsOffsetRef.current, itemsPerLoad));
	//         setSelectedPortfolioVesselKeys(new Set([]));
	//     }
	// };

	const renderPortfolioVesselsInfo = useMemo(() => {
		if (portfolioVessels.length === 0 && errors.portfolioVesselsImoList?.message) {
			return (
				<Chip size="sm" color="danger" variant="light" className="h-8">
					{errors.portfolioVesselsImoList?.message?.toString()}
				</Chip>
			);
		} else {
			return (
				<Chip size="sm" variant="bordered" className="h-8 min-w-full rounded border-msi-chart-orange border">
					{portfolioVessels.length > 0
						? `There are ${portfolioVessels.length} vessels in the portfolio`
						: 'There is no vessel in the portfolio'}
				</Chip>
			);
		}
	}, [portfolioVessels, errors]);

	return (
		<div className="flex flex-row h-full">
			<div className="flex-1">
				<Divider className=" my-0 bg-gray-200 " orientation="vertical" />
			</div>

			<div className="flex flex-col">
				<div className="flex-initial">
					<div className="px-3 py-1 items-center text-center">
						<Label title="Portfolio Vessel Selection" textSize="lg" />
						<span className="cursor-pointer float-right" onClick={closePortfolioDetail}>
							<FontAwesomeIcon icon={faClose} size="sm" />
						</span>
					</div>
				</div>
				<Divider className="w-auto mx-2 mt-1" />
				<div className="flex flex-row h-full overflow-y-hidden mt-1 mb-2 mx-2 space-x-2">
					<VesselsSelectbox
						listboxRef={msiVesselsListboxRef}
						key={`subscribed-vessels`}
						title="MSI Vessels"
						options={leftItems}
						selectionMode="multiple"
						hideSelectedIcon={false}
						applySearchTerm={filterMsiVesselsOnSearch}
						applyScroll={loadMsiVesselsOnScroll}
						updateSelectedKeys={updateSelectedMsiVesselKeys}
						selectedKeys={useMemo(() => Array.from(selectedMsiVesselsKeys), [selectedMsiVesselsKeys])}
					>
						<FileUpload uploadData={filterMsiVesselsOnUpload} />
					</VesselsSelectbox>
					{/* 
                    <div className="flex flex-col justify-center  space-y-2">
                        <Button className='h-6 rounded' size="sm" onPress={moveToPortfolioVessels} isDisabled={Array.from(selectedMsiVesselsKeys).length === 0}>
                            &gt;
                        </Button>
                        <Button className='h-6 rounded' size="sm" onPress={removeFromMsiVessels} isDisabled={Array.from(selectedPortfolioVesselKeys).length === 0}>
                            &lt;
                        </Button>
                    </div> */}
					<VesselsSelectbox
						listboxRef={portfolioVesselsListboxRef}
						key={`portfolio-vessels-${portfolioId}`}
						isLoading={isPortfolioWithVesselsLoading}
						title="Portfolio Vessels"
						options={rightItems}
						selectionMode="single"
						hideSelectedIcon={true}
						applySearchTerm={filterPortfolioVesselsOnSearch}
						applyScroll={loadPortfolioVesselsOnScroll}
						updateSelectedKeys={updateSelectedPortfolioVesselKeys}
					>
						{renderPortfolioVesselsInfo}
					</VesselsSelectbox>
				</div>
				<form className="flex-1">
					<div className="flex-initial flex flex-row items-start space-x-2 bg-msi-white-color px-2 py-4 ">
						<SelectionSave
							onSubmit={handleSubmit(onSaveSubmit)}
							isEditAllowed={selectedPortfolioWithVessels?.isOwnPortfolio ?? true}
							register={register}
							errors={errors}
							label="Save Portfolio:"
							selectedOption={selectedPortfolioWithVessels?.scope}
							name={selectedPortfolioWithVessels?.name}
							portfolioNames={portfolioNames}
						/>
						<input
							type="hidden"
							{...register('portfolioVesselsImoList', {
								validate: (value) => (value && value.length > 0) || 'At least one vessel is required',
							})}
						/>
					</div>

					<Divider className="w-auto mx-2 mt-1 bg-gray-200" />
					<div className="flex-initial flex py-4 px-2 justify-end space-x-4 bg-msi-white-color">
						<SelectionApply displayOutput={handleSubmit(onDisplayPortfolioOutputSubmit)} discardChanges={discardChanges} />
					</div>
				</form>
			</div>
			<SharedSpinner size="lg" textSize="text-medium" isBusy={isBusy} label="Saving portfolio..." />
		</div>
	);
};

export default PortfolioDetailPanel;
